import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { InfoIcon } from '@phosphor-icons/react'
import ReactTooltip from 'react-tooltip'
import { Trans, useTranslation } from 'react-i18next'

import TextField from '../../../../../components/FormLegacy/FormFieldsV2/TextField'
import SelectField from '../../../../../components/FormLegacy/FormFieldsV2/SelectField'
import ToggleField from '../../../../../components/FormLegacy/FormFieldsV2/ToggleField'
import MediaUpload from '../../../../../components/MediaUpload/MediaUpload'
import CheckboxField from '../../../../../components/FormLegacy/FormFieldsV2/CheckboxField'
import { formatNumberOfDaysText } from '../../../../../helpers/helpers'
import {
  getNamedHandler,
  getChecklistAssignTriggerTypeOptions,
  getContinueStartTriggers,
} from '../../helpers'
import Icon from '../../../../../components/Icons'

const MAX_NAME_LENGTH = 50
const ASSIGN_MIN_DAYS = 0
const DUE_DATE_MIN_DAYS = 1
const MAX_DAYS = 1000

// Note: `name` stays untranslated - these are partner brand names. Only the description,
// which is prose, goes through i18n.
const INTEGRATION_META = {
  'public-grid': {
    name: 'Public Grid',
    descriptionKey: 'checklists-create-task:taskSettings.integrationDescriptions.publicGrid',
  },
  light: {
    name: 'Light',
    descriptionKey: 'checklists-create-task:taskSettings.integrationDescriptions.light',
  },
}

const TaskSettings = ({
  formState,
  isLoading,
  isEdit,
  isIntegrationTask,
  isAdmin,
  availableStatuses = [],
  nameError,
  amenities,
  getAmenities,
  buildingId,
  onSubmit = () => {},
  handleFieldChange = () => {},
  handleBack = () => {},
}) => {
  const { t } = useTranslation(['checklists-create-task', 'common'])
  const checklistAssignTriggerTypeOptions = getChecklistAssignTriggerTypeOptions(t)
  const continueStartTriggers = getContinueStartTriggers(t)
  const onFileUpload = (file) => {
    if (file) {
      handleFieldChange({ target: { name: 'attachment', value: file } })
    }
  }

  const handleChangeDays = (e) => {
    let value = parseInt(e.target.value)
    if (value > MAX_DAYS) return
    e.target.value = value
    return handleFieldChange(e)
  }

  const onHandleNameField = getNamedHandler(handleFieldChange)

  const handleNameChange = (e) => {
    if (e.target.value.length < MAX_NAME_LENGTH) {
      handleFieldChange(e)
    }
  }

  const isAmenityReservationStarted = formState.assignTaskTrigger === 'amenity-reservation-started'
  const integrationMeta = INTEGRATION_META[formState.integrationProvider] ?? null

  useEffect(() => {
    getAmenities(buildingId)
  }, [])

  const amenitiesOptions = useMemo(
    () =>
      amenities.length
        ? amenities.map((item) => ({ value: item.id, label: item.name }))
        : amenities,
    [amenities],
  )

  return (
    <React.Fragment>
      <div
        className={`checklist-modal-content checklist-task-data ${isLoading ? 'is-loading' : ''}`}
        style={{ gap: 12 }}
      >
        {isIntegrationTask && integrationMeta && (
          <div className="integration-task-desc">
            <div>
              <Icon icon="replay" />
            </div>
            <div>
              <h3>
                <Trans
                  t={t}
                  i18nKey="checklists-create-task:taskSettings.integratedWith"
                  values={{ name: integrationMeta.name }}
                  components={[<u key="u" />]}
                />
              </h3>
              <span>{t(integrationMeta.descriptionKey)}</span>
            </div>
          </div>
        )}
        {isIntegrationTask && (
          <div className="checklist-modal-input-row">
            <div className="input-container">
              <div className="input-label">
                {t('checklists-create-task:customTaskForm.taskNameLabel')}
              </div>
              <TextField
                name="name"
                onChange={handleNameChange}
                value={formState.name}
                placeholder={t('checklists-create-task:customTaskForm.taskNamePlaceholder')}
                style={{ width: '100%' }}
                isError={!!nameError}
                errorLabel={nameError}
              />
              <span className="input-desc-label">
                {t('checklists-create-task:customTaskForm.maxLengthInfo', {
                  length: MAX_NAME_LENGTH,
                })}
              </span>
            </div>
          </div>
        )}
        {!isIntegrationTask && (
          <div className="checklist-modal-input-row">
            <div className="input-container">
              <div className="input-label small">
                {t('checklists-create-task:customTaskForm.uploadPdfLabel')}
              </div>
              <MediaUpload files={[formState.attachment]} onUpdate={onFileUpload} />
              <span className="input-desc-label">
                {t('checklists-create-task:taskSettings.fileFormatInfo')}
              </span>
            </div>
          </div>
        )}
        <div className="checklist-modal-input-row">
          <div className="input-container">
            <ToggleField
              label={t('checklists-create-task:customTaskForm.setAssignTriggerLabel')}
              name="assign-trigger-toggle"
              labelOnRight
              onChange={() =>
                onHandleNameField(!formState.assignTriggerVisibility, 'assignTriggerVisibility')
              }
              value={formState.assignTriggerVisibility}
            />
          </div>
        </div>
        {formState.assignTriggerVisibility && (
          <div className="checklist-modal-input-row">
            <div className="inline-row" style={{ gap: 8 }}>
              <div className="input-container" style={{ width: '100%' }}>
                <div className="input-label small">
                  {t('checklists-create-task:taskSettings.numberOfDaysShortLabel')}
                </div>
                <TextField
                  name="assignTaskDays"
                  type="number"
                  onChange={handleChangeDays}
                  onBlur={() =>
                    handleFieldChange({
                      target: {
                        name: 'assignTaskDays',
                        value: parseInt(formState.assignTaskDays) || ASSIGN_MIN_DAYS,
                      },
                    })
                  }
                  value={formState.assignTaskDays}
                  min={ASSIGN_MIN_DAYS}
                />
              </div>
              <div className="input-container" style={{ width: '100%' }}>
                <div className="input-label small">
                  {t('checklists-create-task:customTaskForm.relativeToTriggerLabel')}
                </div>
                <SelectField
                  name="assignTaskRelative"
                  options={continueStartTriggers}
                  onChange={handleFieldChange}
                  value={formState.assignTaskRelative}
                />
              </div>
              <div className="input-container" style={{ width: '100%' }}>
                <div className="input-label small">
                  {t('checklists-create-task:customTaskForm.selectTriggerLabel')}
                </div>
                <SelectField
                  name="assignTaskTrigger"
                  options={checklistAssignTriggerTypeOptions}
                  onChange={handleFieldChange}
                  value={formState.assignTaskTrigger}
                />
                {isAmenityReservationStarted && (
                  <React.Fragment>
                    <div className="input-label small">
                      {t('checklists-create-task:taskSettings.selectAmenityLabel')}
                    </div>
                    <SelectField
                      name="amenityId"
                      options={amenitiesOptions}
                      placeholder={t(
                        'checklists-create-task:taskSettings.selectAmenityPlaceholder',
                      )}
                      onChange={handleFieldChange}
                      value={formState.amenityId}
                    />
                  </React.Fragment>
                )}
              </div>
            </div>
            <span className="input-desc-label">
              {t('checklists-create-task:customTaskForm.assignedDescription', {
                days: formatNumberOfDaysText(formState.assignTaskDays, t),
                relative: formState.assignTaskRelative,
                trigger: formState.assignTaskTrigger,
              })}
            </span>
          </div>
        )}
        <div className="checklist-modal-input-row">
          <div className="input-container inline">
            <CheckboxField
              name={'limitTaskAssignmentVisibility'}
              value={formState.limitTaskAssignmentVisibility}
              onChange={(e) => onHandleNameField(e.target.checked, 'limitTaskAssignmentVisibility')}
              label={t('checklists-create-task:taskSettings.limitTaskAssignmentLabel')}
            />
          </div>
        </div>
        {formState.limitTaskAssignmentVisibility && (
          <div className="checklist-modal-input-row">
            <div className="input-container">
              <SelectField
                name="assignTaskStatusTrigger"
                options={availableStatuses}
                value={formState.assignTaskStatusTrigger}
                onChange={handleFieldChange}
                placeholder={t(
                  'checklists-create-task:taskSettings.selectResidentStatusPlaceholder',
                )}
                style={{ width: '280px' }}
              />
            </div>
          </div>
        )}
        <div className="checklist-modal-input-row">
          <div className="input-container" style={{ width: '100%' }}>
            <ToggleField
              label={t('checklists-create-task:customTaskForm.setDueDateLabel')}
              name="due-date-toggle"
              labelOnRight
              onChange={() =>
                onHandleNameField(!formState.dueDateTriggerVisibility, 'dueDateTriggerVisibility')
              }
              value={formState.dueDateTriggerVisibility}
              disabled={formState.serviceTask}
            />
          </div>
        </div>
        {formState.dueDateTriggerVisibility && (
          <div className="checklist-modal-input-row">
            <div className="inline-row" style={{ gap: 8 }}>
              <div className="input-container" style={{ width: '100%' }}>
                <div className="input-label small">
                  {t('checklists-create-task:taskSettings.numberOfDaysShortLabel')}
                </div>
                <TextField
                  name="dueDateDays"
                  type="number"
                  onChange={handleChangeDays}
                  onBlur={() =>
                    handleFieldChange({
                      target: {
                        name: 'dueDateDays',
                        value: parseInt(formState.dueDateDays) || DUE_DATE_MIN_DAYS,
                      },
                    })
                  }
                  value={formState.dueDateDays}
                  min={DUE_DATE_MIN_DAYS}
                />
              </div>
              <div className="input-container" style={{ width: '100%' }}>
                <div className="input-label small">
                  {t('checklists-create-task:customTaskForm.relativeToTriggerLabel')}
                </div>
                <SelectField
                  name="dueDateCondition"
                  options={continueStartTriggers}
                  onChange={handleFieldChange}
                  value={formState.dueDateCondition}
                />
              </div>
              <div className="input-container" style={{ width: '100%' }}>
                <div className="input-label small">
                  {t('checklists-create-task:customTaskForm.selectTriggerLabel')}
                </div>
                <SelectField
                  name="dueDateTrigger"
                  options={checklistAssignTriggerTypeOptions.filter(
                    (option) => option.value !== 'amenity-reservation-started',
                  )}
                  onChange={handleFieldChange}
                  value={formState.dueDateTrigger}
                />
              </div>
            </div>
            <span className="input-desc-label">
              {t('checklists-create-task:customTaskForm.completedWithinDescription', {
                days: formatNumberOfDaysText(formState.dueDateDays, t),
                condition: formState.dueDateCondition,
                trigger: formState.dueDateTrigger,
              })}
            </span>
          </div>
        )}
        {!isIntegrationTask && (
          <div className="checklist-modal-input-row">
            <div className="input-container">
              <ToggleField
                label={t('checklists-create-task:taskSettings.requiresApprovalLabel')}
                name="due-requires-approval"
                labelOnRight
                onChange={() => onHandleNameField(!formState.approvalRequired, 'approvalRequired')}
                value={formState.approvalRequired}
                disabled={formState.serviceTask}
              />
            </div>
          </div>
        )}
        {!isIntegrationTask && (
          <div className="checklist-modal-input-row">
            <div className="input-container">
              <ToggleField
                label={t('checklists-create-task:taskSettings.requireSignatureLabel')}
                name="due-requires-signature"
                labelOnRight
                onChange={() =>
                  onHandleNameField(!formState.signatureRequired, 'signatureRequired')
                }
                value={formState.signatureRequired}
                disabled={formState.serviceTask}
              />
            </div>
          </div>
        )}
        {isAdmin && (
          <div className="checklist-modal-input-row">
            <div className="input-container input-container--service-task">
              <ToggleField
                label={t('checklists-create-task:taskSettings.serviceTaskLabel')}
                name="service-task-toggle"
                labelOnRight
                onChange={() => onHandleNameField(!formState.serviceTask, 'serviceTask')}
                value={formState.serviceTask}
              />
              <div data-tip data-for="service-task-tip">
                <InfoIcon size={24} />
                <ReactTooltip id="service-task-tip" open place="right" effect="solid" multiline>
                  <div style={{ maxWidth: '432px' }}>
                    {t('checklists-create-task:taskSettings.serviceTaskTooltip')}
                  </div>
                </ReactTooltip>
              </div>
            </div>
          </div>
        )}
      </div>
      {isEdit ? (
        <div className="checklist-modal-actions">
          <button className="button button-gray" onClick={handleBack}>
            {t('common:actions.back')}
          </button>
          <button
            className="button button-primary auto-width"
            disabled={isLoading}
            onClick={onSubmit}
          >
            {t('common:actions.save')}
          </button>
        </div>
      ) : (
        <div className="checklist-modal-actions">
          <button className="button button-gray" onClick={handleBack}>
            {t('common:actions.back')}
          </button>
          <button
            className="button button-primary auto-width"
            disabled={isLoading}
            onClick={onSubmit}
          >
            {t('checklists-create-task:customTaskForm.createTaskButton')}
          </button>
        </div>
      )}
    </React.Fragment>
  )
}

TaskSettings.propTypes = {
  formState: PropTypes.object,
  isLoading: PropTypes.bool,
  isEdit: PropTypes.bool,
  isIntegrationTask: PropTypes.bool,
  isAdmin: PropTypes.bool,
  nameError: PropTypes.string,
  availableStatuses: PropTypes.array,
  handleFieldChange: PropTypes.func,
  handleBack: PropTypes.func,
  onSubmit: PropTypes.func,
  getAmenities: PropTypes.func,
  amenities: PropTypes.array,
  buildingId: PropTypes.number,
}

export default TaskSettings
